/* Tip Split Calculator copy, keyed by locale.
 *
 * Spanish is deliberately deferred to v2 (Daniel's call). The shape is here so
 * v2 is a fill-in job, not a refactor: every string the tool renders, including
 * the printed receipt and the PDF, is read from this bundle. Nothing in the
 * component hardcodes English.
 *
 * When the `es` set is written: register is respectful-informal, key terms are
 * propinas, fondo de propinas, puntos, horas, reparto. The disclaimer below has
 * a fixed Spanish wording already agreed on; it is parked in DISCLAIMER_ES so
 * it does not get re-translated by hand later.
 */

export const DISCLAIMER_ES =
  "Esta herramienta hace las cuentas. Las reglas de reparto de propinas varian segun el estado y la situacion; como repartir es su decision y su responsabilidad.";

const en = {
  meta: {
    title: "Free Tip Split Calculator - Pool by Hours or Tip-Out | Daniel Ray Rangel",
    description:
      "Free tip split calculator for bars and restaurants. Split a tip pool by weighted hours, or run tip-outs to bar, barbacks, bussers, runners, and hosts. Shows the math, prints a receipt, works on your phone. Nothing leaves your browser.",
    ogTitle: "Free Tip Split Calculator - Pool by Hours or Tip-Out",
    ogDescription:
      "Split a tip pool by weighted hours or run tip-outs to support staff. Shows the math on a printable receipt. No sign-up, nothing leaves your browser.",
  },

  hero: {
    backLink: "More free tools",
    badges: ["Free Tool", "No sign-up", "Nothing leaves your phone"],
    titleLine1: "Split the tips,",
    titleLine2: "down to the cent.",
    sub: "Put in who worked and what the pool was. Get every share, the arithmetic behind it, and a receipt you can print and hand out. The cents always add up to the pool, and none of it leaves your browser.",
  },

  modes: {
    label: "How do you split?",
    pool: "Pool & split",
    tipout: "Tip-out",
    poolHint:
      "One pot, split by hours and points. Put the pool total in below, then check off who worked and fill in their hours.",
    tipoutHint:
      "Everyone keeps their own tips and pays a percentage to the support roles. Start by writing your tip-out rules in step 1 — nothing can be asked of anybody until a rule exists.",
  },

  // Money and rules first, because that is the part somebody wants to punch in
  // standing at the pass mid-shift. The roster is a close-out job.
  steps: {
    tonight: "Tonight",
    crew: "Who worked",
    shift: "The shift",
    split: "The split",
    // Each panel says what it wants in the mode you are actually in, because
    // "The shift" means two different sets of boxes in the two modes.
    tonightSub: {
      pool: "What the pool came to, and what to call this printout.",
      tipout: "Who pays what to whom. Nothing can be asked of anybody until a rule exists.",
    },
    crewSub: {
      pool: "Check off tonight's crew. Points set who earns more per hour.",
      tipout: "Check off tonight's crew. Checked off is all it takes to get a share of a support pool.",
    },
    shiftSub: {
      pool: "Hours for everyone who worked.",
      tipout: "What each person brought in. Your rules decide which boxes appear.",
    },
    splitSub: {
      pool: "Every share, and the arithmetic behind it.",
      tipout: "Each person's ledger, then each support pool.",
    },
  },

  // The thing the tool never said out loud: the order to do this in.
  guide: {
    title: "How this works",
    pool: [
      "Put the pool total in at the top.",
      "Add the crew and check off who worked. Points are a per-hour multiplier — 1.0 is a full share, 0.5 is half.",
      "Fill in each person's hours.",
      "Read the split, then share it or print it.",
    ],
    tipout: [
      "Write your rules at the top: who pays what percentage of what, to whom. Nothing else happens until a rule exists.",
      "Add the crew and check off who worked. Checking somebody off is enough to get them a share of a support pool.",
      "Fill in what each person brought in. The form asks each of them only for what their own rules need — tips if they pay in, food or liquor sales if a rule reads them. Support roles get an \"own tips\" box for money handed straight to them; leave it blank if they took none.",
      "Read each ledger and each support pool, then share it or print it.",
    ],
  },

  roster: {
    pointsExplainer:
      "Points size somebody's share of a pool. Standard is 1.0, half a share is 0.5, and only the ratio matters — a house where everyone is on 2.0 splits exactly like a house where everyone is on 1.0. In pool and split they multiply the hours, so six hours at 0.5 is paid the same as three hours at 1.0. In tip-out they carry the split on their own unless you fill hours in.",
    // The box goes flat when nothing can read it, rather than sitting there
    // fully editable next to a footnote saying it does nothing.
    pointsUnused: "Not used",
    pointsUnusedWhy: "No rule pays this role out of a pool, so points cannot change anything here.",
    name: "Name",
    role: "Role",
    points: "Points",
    hours: "Hours",
    tips: "Tips",
    // Asked of the support roles, who are handed money directly all night and
    // get their tip-out on top of it. Named apart from plain "Tips" so nobody
    // reads it as the share the pool below is about to pay them, and kept to
    // two words so the caption stays on one line in a phone-width column.
    ownTips: "Own tips",
    sales: "Total sales",
    foodSales: "Food sales",
    liquorSales: "Liquor sales",
    working: "Worked tonight",
    optional: "optional",
    // The tick's own caption. On a phone the column headers are gone, so it
    // needs a word next to it, and the column is one thumb wide.
    workedShort: "Worked",
    add: "Add person",
    remove: "Remove",
    hide: "Hide",
    show: "Show",
    collapsed: (working, total) =>
      total === 1
        ? `${working} of 1 person working`
        : `${working} of ${total} people working`,
    empty: "Nobody on the roster yet. Add the crew once and it is here next shift.",
    namePlaceholder: "Name",
    manageRoles: "Roles and default points",
    roleName: "Role",
    roleDefaultPoints: "Default points",
    addRole: "Add role",
    roleInUse: "In use by someone on the roster",
    saved: "Saved on this device",
    noStorage:
      "This browser is blocking storage, so the roster will not be here next time. Everything else works.",
  },

  rules: {
    title: "Tip-out rules",
    empty:
      "No rules yet, so there is nothing to calculate. Add one to say who pays what to whom — for example, Server pays 10% of tips to Bar.",
    add: "Add rule",
    pays: "pays",
    ofTips: "of tips",
    ofSales: "of total sales",
    ofFood: "of food sales",
    ofLiquor: "of liquor sales",
    to: "to",
    basisTips: "tips",
    basisSales: "total sales",
    basisFood: "food sales",
    basisLiquor: "liquor sales",
    sameRole: "A role cannot pay itself. Pick a different role.",
  },

  shift: {
    labelField: "Shift label",
    labelPlaceholder: "Friday dinner",
    businessField: "Business name (optional)",
    businessPlaceholder: "Prints at the top of the receipt",
    businessHint: "Saved on this device and reused by the other tools.",
    pool: "Tip pool",
    poolPlaceholder: "0.00",
    // Most of a closing crew clocked the same shift, so this is typed once and
    // the two people who did not get corrected after.
    sameHours: "Same hours for everyone",
    applyAll: "Apply to all",
    progress: (done, total) => `${done} of ${total} filled in`,
    noWorkers: "Check off who worked in step 2 to fill in the shift.",
    contributorsHead: "Who brought in tips",
    supportHead: "Who gets paid from the pools",
    noRulesForRole: "No tip-out rules for this role",
    // Shown in step 2 when tip-out mode has no rules yet, because without them
    // there is nothing to ask anyone for.
    needRules:
      "Tip-out mode needs at least one rule before it can ask for numbers. Scroll up to Tip-out rules in step 1 and add one, like Server pays 10% of tips to Bar.",
  },

  results: {
    poolLabel: "Tip pool",
    totalWeighted: "Total weighted hours",
    rate: "Rate per weighted hour",
    rateExact: (rate) => `exact ${rate}`,
    name: "Name",
    role: "Role",
    hours: "Hours",
    points: "Points",
    weighted: "Weighted",
    // The column-header set the on-screen table never grew; the receipt has its
    // own in receipt.col*. "share" is gone from it because the share button
    // below owns that name now.
    total: "Total",
    tips: "Tips",
    takeHome: "Take home",
    poolReceived: "Pool",
    contributors: "Contributors",
    support: "Support staff",
    conservation: (tips, take, shares) =>
      `Total tips ${tips} = take-homes ${take} + shares ${shares}`,
    // Share leads because the phone in the manager's hand is nowhere near a
    // printer. The share sheet is the device's own; nothing is uploaded.
    share: "Share breakdown",
    shareTitle: "Tip split",
    copied: "Copied",
    print: "Print breakdown",
    printBlank: "Print sign sheet",
    download: "Download PDF",
    reset: "Start next shift",
    resetQuestion: "This clears tonight's hours and amounts. Your roster stays.",
    resetConfirm: "Clear this shift",
    cancel: "Cancel",
    undo: "Undo",
    undoReset: "Shift cleared",
    undoRemoved: (name) => `${name} removed`,
    empty: "Fill in the shift above and the split shows up here.",
    // Mode-aware versions of the same idea, so the panel says what is missing
    // instead of a red error on a form nobody has filled in yet.
    needHours: "Put hours in for at least one person and the split shows up here.",
    needRules:
      "Add a tip-out rule in step 1, then put tonight's tips in, and the split shows up here.",
    needTips: "Put tonight's tips in above and the split shows up here.",
    overpaid: "Over-deducted",
    working: "Preparing...",
  },

  // The line that rides the bottom of the workspace. The boxes and the answer
  // cannot both be on a phone screen, so this says what happened to what you
  // typed and takes you to it.
  bar: {
    idle: "Nothing to split yet",
    fixOne: "1 box needs fixing",
    fixMany: (n) => `${n} boxes need fixing`,
    problem: "The split cannot run yet",
    goFix: "Go fix it",
    ready: (n) => (n === 1 ? "Split ready for 1" : `Split ready for ${n}`),
    see: "See the split",
  },

  validation: {
    notNumber: "Numbers only.",
    negative: "Cannot be negative.",
    hoursTooHigh: "More than 24 hours in a shift.",
    hoursBlankAmongHours:
      "Others in this role have hours, so a blank here pays nothing. Leave every one of them blank for an even split.",
    pointsRange: "Unusual — most houses use 0.05 to 3.0.",
    pctTooHigh: "Cannot pay out more than 100%.",
    pctRange: "Unusual — most tip-outs are 0.25% to 25%.",
  },

  errors: {
    ZERO_WEIGHT:
      "Nobody has hours yet, so there is nothing to split by. Add hours for at least one person.",
    NEGATIVE_TAKE_HOME: (d) =>
      `${d.name} would take home less than nothing. The rules on this role add up to more than the tips brought in (${d.rules.join(", ")}). Lower a percentage or take a rule off for tonight.`,
    NO_RECIPIENTS: (d) =>
      `${d.roleName} is owed ${d.amount} tonight but nobody in that role is checked as working. Check someone off, or take that rule off for tonight.`,
    ROLE_ZERO_WEIGHT: (d) =>
      `${d.roleName} is owed ${d.amount} tonight but everyone in that role is set to zero points, so there is nothing to divide it by. Give at least one of them points in step 2.`,
    NEGATIVE_TOTAL:
      "The tip pool cannot be a negative amount. Check the pool box.",
    BAD_WEIGHT:
      "Hours and points cannot be negative. Check the boxes outlined in red.",
    NEGATIVE_BASIS:
      "Tips and sales cannot be negative. Check the boxes outlined in red.",
    BAD_PERCENT:
      "A tip-out percentage cannot be negative. Check the rules in step 1.",
    SELF_RULE: (d) =>
      `${d.roleName} is set to pay itself, which just moves money around inside the role. Point that rule at a different role or take it off.`,
    SUM_MISMATCH:
      "The shares did not add up to the pool, so nothing is being shown. This is a bug. Please tell me what you typed in.",
    CONSERVATION_MISMATCH:
      "The money going out did not match the money coming in, so nothing is being shown. This is a bug. Please tell me what you typed in.",
    BAD_NUMBER: "Check the boxes outlined in red. Numbers only.",
    CHECK_FIELDS: "Check the boxes outlined in red before reading the split.",
    GENERIC: "Something went wrong with the math, so nothing is being shown.",
  },

  receipt: {
    poolTitle: "TIP SPLIT - POOL & SPLIT",
    tipoutTitle: "TIP SPLIT - TIP-OUT",
    blankTitle: "TIP SPLIT - SIGN SHEET",
    pool: "Tip pool",
    totalWeighted: "Total weighted hours",
    rate: "Rate per weighted hour",
    colName: "NAME",
    colRole: "ROLE",
    colHours: "HOURS",
    colPoints: "PTS",
    colWeighted: "WGT HRS",
    colShare: "SHARE",
    colSignature: "SIGNATURE",
    total: "TOTAL",
    contributors: "CONTRIBUTORS",
    support: "SUPPORT STAFF",
    tips: "Tips",
    takeHome: "TAKE HOME",
    poolFor: (role, amount) => `${role} - pool ${amount}`,
    shareFormula: (rate) => `Each share = weighted hours x ${rate}`,
    conservation: (tips, take, shares) =>
      `Total tips ${tips} = take-homes ${take} + shares ${shares}`,
    footnote:
      "Shares are rounded to the cent; leftover cents go to the largest remainders so the total always equals the pool.",
    ruleLine: (pct, basis, role) => `${pct}% of ${basis} to ${role}`,
  },

  disclaimer:
    "This tool does the math. Tip pooling rules vary by state and situation; how you split is your decision and your responsibility.",

  // Under the workspace rather than in the CTA: the person who just hit a wall
  // is looking at the wall, and asking them for it there beats asking after
  // they have scrolled past two more sections and forgotten what it was. It
  // opens in place, too — a tester sent to another page is a tester lost.
  feedback: {
    eyebrow: "Still being built",
    text: "Houses split in ways this cannot write yet. If it got something wrong, or yours needs a rule that is not in here, say so and it gets fixed.",
    button: "Send feedback",
    form: {
      eyebrow: "Tip Split Calculator",
      title: "What should this do differently?",
      sub: "A wrong number, a rule your house runs that this cannot write, a box in the wrong place — all of it is worth sending. It goes straight to me.",
      name: "Name",
      contact: "Email or phone",
      contactPlaceholder: "you@example.com or (480) 555-0123",
      message: "What happened",
      messagePlaceholder: "We tip out 3% of food sales to the kitchen and I could not write that rule...",
      send: "Send it",
      sending: "Sending...",
      cancel: "Cancel",
      close: "Close",
      invalid: "Needs a name, a way to reach you, and a note.",
      error: "That did not send. Try again, or text (480) 282-0569.",
      sent: "Got it — thank you. If it needs a follow-up question, I will reach out at what you left.",
      backToTool: "Back to the split",
      privacy: "Your name and contact come to me and nowhere else. Nothing you typed into the calculator is attached.",
    },
  },

  faqTitle: "Good to know",
  faqs: [
    {
      q: "How do I know the split is right?",
      a: "Every share is calculated in whole cents, never in decimals that drift. Leftover cents go to the largest remainders, so the shares always add up to the pool exactly. The receipt shows the hours, the points, and the rate behind every number, so anyone can check it with a phone calculator.",
    },
    {
      q: "Does anything I type get sent anywhere?",
      a: "No. The math runs inside your browser and nothing is uploaded. Your roster and your business name are saved on this device only, which also means clearing your browser data clears them.",
    },
    {
      q: "Which method should I pick?",
      a: "Pool and split is for houses where all the tips go into one pot and get divided by hours, with points letting some roles earn more or less per hour. Tip-out is for houses where servers keep their own tips and pay a percentage to the bar, the bussers, and the runners.",
    },
    {
      q: "Can a rule use food sales and liquor sales separately?",
      a: "Yes. A rule can read a percentage of tips, of food sales, of liquor sales, or of total sales, so the usual pair — a few percent of food sales to the runners and a few percent of liquor sales to the bar — is two rules with two different bases. Step 2 only asks a person for the figures their own rules actually need.",
    },
    {
      q: "Do tip-outs stack on each other?",
      a: "No. Every percentage is taken off the person's own tips or sales, and money that lands in a support pool is never taxed again on its way out. That is how most houses run it, and it is why the total going out always equals the total coming in.",
    },
    {
      q: "What about credit card fees?",
      a: "This tool splits the numbers you give it. If your house deducts card processing from tips before splitting — and whether that is even allowed depends on your state — enter the tips as the amount that is actually there to divide.",
    },
    {
      q: "My house tips a role that isn't in the list. Can I add it?",
      a: "Yes — the roles list is yours to edit, and what you add is saved on this device for next shift. It starts with Server, Bar, Barback, Bussers, Food runners, and Host because every house tips a different half of that list, and an unused one costs you nothing: no rule points at it, so it asks nobody for anything. Back of house is not in the list on purpose. Whether the kitchen can be in a pool at all turns on whether the house takes a tip credit and on your state, so that is a role to add deliberately rather than one to find sitting there.",
    },
    {
      q: "Is this legal advice about tip pooling?",
      a: "No. Tip rules vary by state and by situation, and some arrangements are not allowed at all. This tool only does the arithmetic on the split you choose. Talk to someone who knows your state's rules before you change how you pay people.",
    },
  ],

  cta: {
    eyebrow: "Phoenix / West Valley / Remote",
    titleLine1: "Need it to remember",
    titleLine2: "every shift?",
    sub: "This tool splits tonight and keeps your roster on one device. If you need shift history, payroll export, and every location in one place, that is what I build.",
    button: "Get in Touch",
  },
};

// v2: fill this in and flip the page to read from the URL locale, the way the
// homepage does. Leaving it empty on purpose beats shipping machine Spanish on
// money labels.
const es = {};

export const STRINGS = { en, es };

export default STRINGS;
